import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';

import { dateFormatter, moneyFormatter, numberFormatter } from '@utils';
import { Credit, FinanceTransaction } from '@types';
import { financeAccountHttp } from '@network';
import { useRequest } from '@hooks';
import { InitialCard, LightTooltip } from '@components';
import Checkbox from '@mui/material/Checkbox';
import cls from './FinancialTransactions.module.css';


interface FinancialTransactionsButtonProps {
  fileIds: number[];
  type: 'invoice' | 'receipt';
  creditCode: string;
}

const FinancialTransactionsButton = ({
  fileIds, type, creditCode,
}: FinancialTransactionsButtonProps) => {

  const { submit: downloadDocuments, loading: isDownloadLoading } = useRequest(async () => {
    await financeAccountHttp.downloadDocuments({
      fileIds,
      type,
      creditCode,
    });
  });

  const isDisabled = fileIds.length === 0;
  const label = type === 'receipt' ? 'View Receipts' : 'View Invoices';

  return (
    <Button
      className={cls.action}
      onClick={downloadDocuments}
      disabled={isDisabled || isDownloadLoading}
    >
      {isDownloadLoading ? 'Loading' : label}
    </Button>
  );
};

export const FinancialTransactions = ({ credit }: {credit: Credit}) => {
  const [transactions, setTransactions] = useState<FinanceTransaction[]>([]);

  useEffect(() => {
    credit && (async () => {
      const nextTransactions = await financeAccountHttp.getFinanceTransactions(credit.id);
      if (nextTransactions && Array.isArray(nextTransactions)) {
        setTransactions(nextTransactions);
      }
    })();
  }, [credit]);

  return (
    <InitialCard left="Transactions" autoHeight extraPadding>
      {transactions.length === 0 && (
        <div className={cls.list}>
          <div className={cls.empty}>No items</div>
        </div>
      )}
      {transactions.length > 0 && (
        <FinanceOperations creditCode={credit.code} transactions={transactions} />
      )}
    </InitialCard>
  );
};

interface FinanceOperationsProps {
  transactions: FinanceTransaction[];
  setTransactions?: (transactions: FinanceTransaction[]) => void;
  creditCode: string;
  addFee?: boolean;
}

export const FinanceOperations = ({
  transactions, creditCode, addFee,
}: FinanceOperationsProps) => {

  return (
    <div className={cls.list}>
      <div className={cls.head}>
        <div className={cls.headContent}>
          <div className={cls.name}>Concept</div>
          <div className={cls.reason}>Purpose</div>
          <div className={cls.date}>Payment date</div>
        </div>
        {addFee && <div className={cls.addFee} />}
        <div className={cls.actions} />
      </div>
      {transactions.length === 0 && (
        <div className={cls.empty}>No items</div>
      )}

      {transactions.map(transaction => {
        const amountPrefix = transaction.type === 'deposit' ? '+ $' : '- $';
        const amount = numberFormatter.format(
          transaction.isUsd ? (transaction.amountUsd ?? 0) : transaction.amount,
        );
        const amountPostfix = transaction.isUsd ? ' USD' : '';
        const key = `${transaction.id}-${amount}`;

        return (
          <div key={key} className={cls.item}>
            <div className={cls.content}>
              <div className={cls.top}>
                <div className={cls.name}>{transaction.code}</div>
                <div>
                  <LightTooltip title="Finance purpose. Operations are used for money calculation">
                    {transaction.purpose === 'Operations' ? (
                      <span className={cls.purposeOperations}>{transaction.purpose}</span>
                    ) : (
                      <span className={cls.purposeCredits}>{transaction.purpose}</span>
                    )}
                  </LightTooltip>
                </div>
                <div className={cls.date}>
                  {transaction.paymentDate && dateFormatter.toDateSlash(transaction.paymentDate)}
                </div>
              </div>
              <div className={cls.bottom}>
                <div className={cls.amount}>
                  {amountPrefix + amount + amountPostfix}
                </div>
                {transaction.exchangeRate && (
                  <div className={cls.exchRate}>
                    Exch. Rate: {moneyFormatter.format(transaction.exchangeRate)}
                  </div>
                )}
                <div className={cls.source}>
                  by {transaction.adminName ? `@${transaction.adminName}` : '[AIRTABLE]'}
                </div>
              </div>
            </div>
            {addFee ? (
              <label className={cls.addFee}>
                {!transaction.code?.includes('Fee') ? (
                  <>
                    <Checkbox checked={transaction.fee} size="large" />
                    <span>Add Fee</span>
                  </>
                ) : null}
              </label>
            ) : null}
            <div className={cls.actions}>
              <FinancialTransactionsButton
                fileIds={transaction?.receipts?.map(r => r.fileId) || []}
                type="receipt"
                creditCode={creditCode}
              />
              <FinancialTransactionsButton
                fileIds={transaction?.invoices?.map(r => r.fileId) || []}
                type="invoice"
                creditCode={creditCode}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
